import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, PencilLine, Plus, Trash2 } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { useRubriques, useCreateRubrique, useDeleteRubrique, useUpdateRubrique } from "@/hooks/useRubriques";
import type { RubriqueDto } from "@/types/rubrique.types";
import { useI18n } from "@/lib/i18n";

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function RubriquesPage() {
  const { t } = useI18n();
  const { data: rubriques, isLoading } = useRubriques();
  const createRubrique = useCreateRubrique();
  const updateRubrique = useUpdateRubrique();
  const deleteRubrique = useDeleteRubrique();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [slug, setSlug] = useState("");
  const [nom, setNom] = useState("");
  const [nomEn, setNomEn] = useState("");
  const [ordre, setOrdre] = useState("");
  const [actif, setActif] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<RubriqueDto | null>(null);

  const rubriqueList = useMemo(() => Array.isArray(rubriques) ? rubriques : [], [rubriques]);

  const resetForm = () => {
    setEditingId(null);
    setSlug("");
    setNom("");
    setNomEn("");
    setOrdre("");
    setActif(true);
  };

  const startEdit = (rubrique: RubriqueDto) => {
    setEditingId(rubrique.id);
    setSlug(rubrique.slug || "");
    setNom(rubrique.nom || "");
    setNomEn(rubrique.nomEn || "");
    setOrdre(rubrique.ordre != null ? String(rubrique.ordre) : "");
    setActif(rubrique.actif);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const payload = {
      slug: slug || slugify(nom),
      nom,
      nomEn: nomEn || undefined,
      ordre: ordre ? Number(ordre) : undefined,
      actif,
    };

    if (!payload.slug || !payload.nom) {
      return;
    }

    const onError = () => toast.error(t("admin.common.saveError"));
    if (editingId) {
      updateRubrique.mutate({ id: editingId, payload }, { onSuccess: resetForm, onError });
    } else {
      createRubrique.mutate(payload, { onSuccess: resetForm, onError });
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteRubrique.mutate(deleteTarget.id, {
      onError: () => toast.error(t("admin.common.deleteError")),
    });
    setDeleteTarget(null);
  };

  return (
    <>
      <AdminTopbar title={t("admin.nav.rubriques")} subtitle={t("admin.rubriques.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <ModulePanel title={t("admin.rubriques.form.title")}>
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Slug</label>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={t("admin.rubriques.form.slugPlaceholder")}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.rubriques.form.nom")}</label>
              <input
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.rubriques.form.nomEn")}</label>
              <input
                value={nomEn}
                onChange={(e) => setNomEn(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.rubriques.form.ordre")}</label>
              <input
                type="number"
                value={ordre}
                onChange={(e) => setOrdre(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5 flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
                {t("admin.rubriques.form.actif")}
              </label>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <button
                type="submit"
                disabled={createRubrique.isPending || updateRubrique.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background"
              >
                {(createRubrique.isPending || updateRubrique.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {editingId ? t("admin.common.save") : t("admin.common.add")}
              </button>
              {editingId ? (
                <button type="button" onClick={resetForm} className="rounded-md border border-border px-4 py-2 text-sm">
                  {t("admin.common.cancel")}
                </button>
              ) : null}
            </div>
          </form>
        </ModulePanel>

        <ModulePanel title={t("admin.rubriques.list.title", { count: rubriqueList.length })}>
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("admin.common.loading")}
            </div>
          ) : rubriqueList.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">{t("admin.rubriques.list.empty")}</div>
          ) : (
            <div className="space-y-2">
              {rubriqueList.map((rubrique) => (
                <div key={rubrique.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface/50 px-3 py-3">
                  <div>
                    <div className="font-medium text-foreground">
                      {rubrique.nom}
                      {!rubrique.actif && <span className="ml-2 text-xs text-muted-foreground">({t("admin.rubriques.inactiveBadge")})</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">slug: {rubrique.slug}{rubrique.ordre != null ? ` · ordre: ${rubrique.ordre}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(rubrique)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs"
                    >
                      <PencilLine className="h-3.5 w-3.5" /> {t("admin.common.edit")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(rubrique)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> {t("admin.common.delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ModulePanel>
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.rubriques.deleteConfirm.title")}
        description={t("admin.rubriques.deleteConfirm.desc")}
        onConfirm={confirmDelete}
      />
    </>
  );
}

export default RubriquesPage;
